import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAngleLeft,
  faAngleRight,
  faChevronDown,
  faNewspaper,
  faTableList,
} from '@fortawesome/free-solid-svg-icons'
import { UserContext } from '../../context/UserContext'

const STORAGE_KEY = 'sidebar-collapsed'

// Pilihan buka/tutup sidebar diingat antar halaman supaya tidak perlu diatur
// ulang setiap kali pindah menu.
const readCollapsed = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true'
  } catch (error) {
    return false
  }
}

const Sidebar = () => {
  const location = useLocation()
  const { user } = useContext(UserContext) ?? {}
  const [collapsed, setCollapsed] = useState(readCollapsed)

  // Menu konten cuma untuk PP, sama seperti sebelumnya.
  const isPermitted = user?.role?.name === 'PP'
  const isKonten = location.pathname.includes('/konten')
  const [isKontenOpen, setIsKontenOpen] = useState(isKonten)

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(collapsed))
    } catch (error) {
      // Penyimpanan browser bisa diblokir; sidebar tetap jalan tanpa itu.
    }
  }, [collapsed])

  // Masuk ke halaman konten lewat tautan mana pun tetap membuka submenunya,
  // supaya posisi halaman yang sedang dibuka selalu kelihatan.
  useEffect(() => {
    if (isKonten) {
      setIsKontenOpen(true)
    }
  }, [isKonten])

  const menus = useMemo(() => {
    const items = [
      {
        key: 'registrasi',
        label: 'Data Registrasi',
        icon: faTableList,
        to: '/',
        isActive: location.pathname === '/',
      },
    ]
    if (isPermitted) {
      items.push({
        key: 'berita',
        label: 'Berita',
        icon: faNewspaper,
        isActive: isKonten,
        children: [
          { label: 'Berita', to: '/konten/berita' },
          { label: 'Pertandingan', to: '/konten/pertandingan' },
          { label: 'Video', to: '/konten/video' },
        ],
      })
    }
    return items
  }, [isPermitted, isKonten, location.pathname])

  const itemClass = (isActive) =>
    `flex items-center gap-3 rounded-xl px-3 h-11 w-full transition-colors ${
      collapsed ? 'justify-center' : ''
    } ${
      isActive ? 'bg-ink text-white' : 'text-muted hover:bg-white/70 hover:text-ink'
    }`

  // Sidebar yang sedang menyempit tidak punya ruang untuk submenu, jadi
  // menekan induknya membuka sidebar sekalian.
  const onToggleParent = () => {
    if (collapsed) {
      setCollapsed(false)
      setIsKontenOpen(true)
      return
    }
    setIsKontenOpen((value) => !value)
  }

  return (
    <div
      className={`flex flex-col gap-2 h-full rounded-card border border-white/70 bg-white/40 shadow-glass p-2 transition-[width] duration-200 ${
        collapsed ? 'w-[72px]' : 'w-[232px]'
      }`}
    >
      <div className="flex items-center gap-2 px-1 py-2">
        <img
          src="https://nos.wjv-1.neo.id/jakmania-bucket/assets/Jakmania%20Landing%20Page/favicon.ico"
          alt="the Jakmania"
          className="object-contain w-9 h-9 flex-none"
        />
        {!collapsed && (
          <span className="font-bold text-ink text-sm truncate">
            the Jakmania
          </span>
        )}
      </div>

      <ul className="flex flex-col gap-1">
        {menus.map((menu) =>
          menu.children ? (
            <li key={menu.key}>
              <button
                type="button"
                title={collapsed ? menu.label : undefined}
                aria-expanded={!collapsed && isKontenOpen}
                onClick={onToggleParent}
                className={itemClass(menu.isActive)}
              >
                <FontAwesomeIcon
                  icon={menu.icon}
                  className="text-base flex-none"
                />
                {!collapsed && (
                  <>
                    <span className="text-sm font-medium truncate flex-1 text-left">
                      {menu.label}
                    </span>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={`text-[11px] flex-none transition-transform ${
                        isKontenOpen ? '' : '-rotate-90'
                      }`}
                    />
                  </>
                )}
              </button>

              {!collapsed && isKontenOpen && (
                <ul className="mt-1 ml-5 pl-3 border-l border-line flex flex-col gap-1">
                  {menu.children.map((child) => {
                    // Cocokkan persis atau sebagai induk path, supaya
                    // /konten/berita-ads tidak ikut menyalakan "Berita".
                    const isChildActive =
                      location.pathname === child.to ||
                      location.pathname.startsWith(`${child.to}/`)
                    return (
                      <li key={child.to}>
                        <Link
                          to={child.to}
                          className={`block rounded-lg px-3 h-9 leading-9 text-sm truncate transition-colors ${
                            isChildActive
                              ? 'bg-white text-ink font-medium shadow-pill'
                              : 'text-muted hover:bg-white/70 hover:text-ink'
                          }`}
                        >
                          {child.label}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              )}
            </li>
          ) : (
            <li key={menu.key}>
              <Link
                to={menu.to}
                title={collapsed ? menu.label : undefined}
                className={itemClass(menu.isActive)}
              >
                <FontAwesomeIcon
                  icon={menu.icon}
                  className="text-base flex-none"
                />
                {!collapsed && (
                  <span className="text-sm font-medium truncate">
                    {menu.label}
                  </span>
                )}
              </Link>
            </li>
          ),
        )}
      </ul>

      <button
        type="button"
        aria-label={collapsed ? 'Buka sidebar' : 'Tutup sidebar'}
        aria-expanded={!collapsed}
        onClick={() => setCollapsed((value) => !value)}
        className={`mt-auto flex items-center gap-3 rounded-xl px-3 h-10 text-muted hover:bg-white/70 hover:text-ink transition-colors ${
          collapsed ? 'justify-center' : ''
        }`}
      >
        <FontAwesomeIcon
          icon={collapsed ? faAngleRight : faAngleLeft}
          className="text-base flex-none"
        />
        {!collapsed && <span className="text-sm font-medium">Tutup</span>}
      </button>
    </div>
  )
}

export default Sidebar
